
import React, { useEffect, useState, useRef } from "react"
import { useNavigate } from "react-router-dom"
import AdminLayout from "@/components/admin/AdminLayout"
import {
    getAdminProducts,
    updateAdminProductBestYn,
    updateAdminProductMainExposureYn,
    updateAdminProductOrders
} from "@/api/adminProductApi"
// 💡 카테고리 목록 조회를 위해 API 임포트 추가
import { getAdminCategories } from "@/api/categoryApi"
import { Button } from "@/components/ui/button"
import { Card, CardContent} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { GripVertical, ChevronUp, ChevronDown, ChevronsUp } from "lucide-react"

type AdminProduct = {
    productId: number
    productName: string
    categoryName: string
    productCode: string
    brandName: string
    price: number
    stockQuantity: number
    status: string
    visibleYn: string
    bestYn: string
    mainExposureYn: string
    createdAt: string
    sortOrder?: number
}

const formatPrice = (value: number) => `${value.toLocaleString("ko-KR")}원`

export default function AdminProductPage() {
    const navigate = useNavigate()

    const [products, setProducts] = useState<AdminProduct[]>([])
    // 💡 카테고리 목록 상태 추가
    const [categories, setCategories] = useState<any[]>([])
    const [loading, setLoading] = useState(false)

    // 💡 검색 상태에 categoryId 추가
    const [search, setSearch] = useState({
        productName: "",
        status: "",
        bestYn: "",
        categoryId: ""
    })

    const [page, setPage] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [totalElements, setTotalElements] = useState(0)
    const [pageSize, setPageSize] = useState(10)

    const dragItem = useRef<number | null>(null)
    const dragOverItem = useRef<number | null>(null)

    // 💡 초기 진입 시 카테고리 목록 세팅 & 상품 조회
    useEffect(() => {
        const init = async () => {
            try {
                const catRes = await getAdminCategories()
                setCategories(catRes?.data?.items || catRes?.items || [])
                fetchProducts(0, pageSize)
            } catch (error) {
                console.error("초기 데이터 로드 실패", error)
            }
        }
        init()
    }, [pageSize])

    const fetchProducts = async (targetPage = page, targetSize = pageSize) => {
        try {
            setLoading(true)
            const response = await getAdminProducts({
                productName: search.productName || undefined,
                status: search.status || undefined,
                bestYn: search.bestYn || undefined,
                categoryId: search.categoryId ? Number(search.categoryId) : undefined, // 💡 카테고리 ID 파라미터 추가
                page: targetPage,
                size: targetSize,
            })

            if (response.success) {
                setProducts(response.data.items || [])
                setTotalPages(response.data.totalPages || 0)
                setTotalElements(response.data.totalElements || 0)
                setPage(targetPage)
            }
        } catch (error) {
            console.error(error)
            alert("상품 목록 조회 중 오류가 발생했습니다.")
        } finally {
            setLoading(false)
        }
    }

    const handleSearch = () => fetchProducts(0, pageSize)
    const handlePageChange = (newPage: number) => {
        if (newPage < 0 || newPage >= totalPages) return
        fetchProducts(newPage, pageSize)
    }

    const saveOrderToDB = async (currentProducts: AdminProduct[]) => {
        try {
            const payload = currentProducts.map((p, index) => ({
                productId: p.productId,
                sortOrder: (page * pageSize) + index
            }))
            await updateAdminProductOrders(payload)
        } catch (error) {
            alert("순서 저장 중 오류가 발생했습니다.")
            fetchProducts(page, pageSize)
        }
    }

    const handleMoveToTop = async (productId: number) => {
        if (!window.confirm("이 상품을 1페이지 최상단으로 이동하시겠습니까?")) return;
        try {
            const topSortOrder = -Math.floor(Date.now() / 1000);
            await updateAdminProductOrders([{ productId, sortOrder: topSortOrder }]);
            alert("1페이지 최상단으로 이동되었습니다.");
            fetchProducts(0, pageSize);
        } catch (error) {
            alert("이동에 실패했습니다.");
        }
    }

    const handleDirectOrderChange = async (productId: number, newRank: number) => {
        if (!newRank || newRank < 1) return;
        if (!window.confirm(`이 상품을 전체 ${newRank}번째 순서로 이동하시겠습니까?`)) return;

        try {
            await updateAdminProductOrders([{ productId, sortOrder: newRank - 1 }]);
            alert("이동되었습니다. (해당 위치 페이지에서 확인 가능합니다)");
            fetchProducts(page, pageSize);
        } catch (error) {
            alert("순서 이동에 실패했습니다.");
        }
    }

    const handleMoveUp = async (index: number) => {
        if (index === 0) return;
        const newProducts = [...products];
        const temp = newProducts[index - 1];
        newProducts[index - 1] = newProducts[index];
        newProducts[index] = temp;
        setProducts(newProducts);
        await saveOrderToDB(newProducts);
    }

    const handleMoveDown = async (index: number) => {
        if (index === products.length - 1) return;
        const newProducts = [...products];
        const temp = newProducts[index + 1];
        newProducts[index + 1] = newProducts[index];
        newProducts[index] = temp;
        setProducts(newProducts);
        await saveOrderToDB(newProducts);
    }

    const handleDragStart = (e: React.DragEvent<HTMLTableRowElement>, index: number) => {
        dragItem.current = index
        e.dataTransfer.effectAllowed = "move"
        if (e.target instanceof HTMLElement) e.target.style.opacity = "0.5"
    }
    const handleDragEnter = (e: React.DragEvent<HTMLTableRowElement>, index: number) => {
        e.preventDefault()
        dragOverItem.current = index
    }
    const handleDragEnd = async (e: React.DragEvent<HTMLTableRowElement>) => {
        if (e.target instanceof HTMLElement) e.target.style.opacity = "1"
        if (dragItem.current !== null && dragOverItem.current !== null && dragItem.current !== dragOverItem.current) {
            const newProducts = [...products]
            const draggedItemContent = newProducts.splice(dragItem.current, 1)[0]
            newProducts.splice(dragOverItem.current, 0, draggedItemContent)
            setProducts(newProducts)
            await saveOrderToDB(newProducts)
        }
        dragItem.current = null
        dragOverItem.current = null
    }

    const handleToggleBest = async (e: React.MouseEvent, productId: number, currentBest: string) => {
        e.stopPropagation()
        const nextBest = currentBest === 'Y' ? 'N' : 'Y'
        if (!window.confirm(nextBest === 'Y' ? '베스트 상품으로 설정하시겠습니까?' : '베스트 설정을 해제하시겠습니까?')) return
        try {
            await updateAdminProductBestYn(productId, nextBest)
            setProducts((prev) => prev.map((p) => p.productId === productId ? { ...p, bestYn: nextBest, mainExposureYn: nextBest === 'Y' ? 'Y' : p.mainExposureYn } : p))
        } catch (error) { alert("상태 변경에 실패했습니다.") }
    }

    const handleToggleMainExposure = async (e: React.MouseEvent, productId: number, currentStatus: string) => {
        e.stopPropagation()
        const nextStatus = currentStatus === 'Y' ? 'N' : 'Y'
        if (!window.confirm(nextStatus === 'Y' ? '메인 화면에 노출하시겠습니까?' : '메인 화면 노출을 해제하시겠습니까?')) return
        try {
            await updateAdminProductMainExposureYn(productId, nextStatus)
            setProducts((prev) => prev.map((p) => p.productId === productId ? { ...p, mainExposureYn: nextStatus } : p ))
        } catch (error) { alert("상태 변경에 실패했습니다.") }
    }

    return (
      <AdminLayout>
          <div className="space-y-6">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                      <h2 className="text-3xl font-bold">상품관리</h2>
                      <p className="mt-2 text-sm text-slate-500">
                          상품 등록, 수정, 재고 상태 및 <strong>노출 순번</strong>을 관리합니다.
                      </p>
                  </div>
                  <Button className="rounded-xl bg-slate-900 hover:bg-slate-800" onClick={() => navigate("/admin/products/new")}>
                      상품 등록
                  </Button>
              </div>

              <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                  <CardContent className="pt-6">
                      {/* 💡 검색바 레이아웃 조정 및 카테고리 셀렉트 박스 추가 */}
                      <div className="flex flex-wrap gap-3 items-center">
                          <Input
                            placeholder="상품명 또는 상품코드 검색"
                            className="rounded-xl min-w-[200px] flex-1"
                            value={search.productName}
                            onChange={(e) => setSearch((prev) => ({ ...prev, productName: e.target.value }))}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                          />

                          {/* 💡 카테고리 필터 */}
                          <select
                            className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none w-36"
                            value={search.categoryId}
                            onChange={(e) => setSearch((prev) => ({ ...prev, categoryId: e.target.value }))}
                          >
                              <option value="">전체 카테고리</option>
                              {categories.map(cat => (
                                <option key={cat.categoryId} value={cat.categoryId}>
                                    {cat.name}
                                </option>
                              ))}
                          </select>

                          <select className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none w-32" value={search.bestYn} onChange={(e) => setSearch((prev) => ({ ...prev, bestYn: e.target.value }))}>
                              <option value="">베스트 전체</option><option value="Y">베스트 (Y)</option><option value="N">일반 (N)</option>
                          </select>

                          <select className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none w-32" value={search.status} onChange={(e) => setSearch((prev) => ({ ...prev, status: e.target.value }))}>
                              <option value="">전체 상태</option><option value="SELLING">판매중</option><option value="SOLD_OUT">품절</option><option value="HIDDEN">숨김</option>
                          </select>

                          <select className="h-10 rounded-xl border border-slate-200 bg-slate-50 font-medium px-3 text-sm outline-none cursor-pointer w-32" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                              <option value={10}>10개씩 보기</option><option value={30}>30개씩 보기</option><option value={50}>50개씩 보기</option><option value={100}>100개씩 보기</option>
                          </select>

                          <Button className="rounded-xl bg-slate-900 text-white w-24" onClick={handleSearch}>검색</Button>
                      </div>
                  </CardContent>
              </Card>

              <Card className="rounded-[28px] border border-slate-200 shadow-sm overflow-hidden">
                  <CardContent className="p-0">
                      <div className="overflow-x-auto">
                          <table className="w-full min-w-[1100px] text-sm select-none">
                              <thead>
                              <tr className="border-b border-slate-200 bg-slate-50 text-left text-slate-500">
                                  <th className="px-3 py-4 font-medium w-20 text-center">순서</th>
                                  <th className="px-4 py-4 font-medium">상품ID</th>
                                  <th className="px-4 py-4 font-medium">상품명</th>
                                  <th className="px-4 py-4 font-medium">카테고리</th>
                                  <th className="px-4 py-4 font-medium">상품코드</th>
                                  <th className="px-4 py-4 font-medium">브랜드</th>
                                  <th className="px-4 py-4 font-medium">판매가</th>
                                  <th className="px-4 py-4 font-medium">재고</th>
                                  <th className="px-4 py-4 font-medium">상태</th>
                                  <th className="px-4 py-4 font-medium">노출</th>
                                  <th className="px-4 py-4 font-medium">메인노출</th>
                                  <th className="px-4 py-4 font-medium">베스트</th>
                                  <th className="px-4 py-4 font-medium">등록일시</th>
                              </tr>
                              </thead>
                              <tbody>
                              {loading ? (
                                <tr><td colSpan={13} className="px-4 py-8 text-center text-slate-500">조회 중...</td></tr>
                              ) : products.length === 0 ? (
                                <tr><td colSpan={13} className="px-4 py-8 text-center text-slate-500">등록된 상품이 없습니다.</td></tr>
                              ) : (
                                products.map((product, index) => (
                                  <tr
                                    key={product.productId}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    onDragEnter={(e) => handleDragEnter(e, index)}
                                    onDragEnd={handleDragEnd}
                                    onDragOver={(e) => e.preventDefault()}
                                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                                  >
                                      <td className="px-2 py-2 text-center align-middle">
                                          <div className="flex flex-col items-center justify-center gap-0.5">
                                              <button type="button" onClick={(e) => { e.stopPropagation(); handleMoveToTop(product.productId); }} className="text-indigo-400 hover:text-indigo-600 transition-colors cursor-pointer mb-0.5" title="1페이지 최상단으로 이동">
                                                  <ChevronsUp size={18} />
                                              </button>
                                              <button type="button" onClick={(e) => { e.stopPropagation(); handleMoveUp(index); }} disabled={index === 0} className="text-slate-400 hover:text-indigo-600 disabled:opacity-20 disabled:hover:text-slate-400 transition-colors cursor-pointer" title="한 칸 위로">
                                                  <ChevronUp size={18} />
                                              </button>
                                              <div className="text-slate-300 hover:text-indigo-500 cursor-grab active:cursor-grabbing my-0.5" title="드래그로 순서 변경">
                                                  <GripVertical size={16} className="mx-auto" />
                                              </div>
                                              <button type="button" onClick={(e) => { e.stopPropagation(); handleMoveDown(index); }} disabled={index === products.length - 1} className="text-slate-400 hover:text-indigo-600 disabled:opacity-20 disabled:hover:text-slate-400 transition-colors cursor-pointer" title="한 칸 아래로">
                                                  <ChevronDown size={18} />
                                              </button>
                                              <Input
                                                key={(page * pageSize) + index + 1}
                                                type="number"
                                                defaultValue={(page * pageSize) + index + 1}
                                                className="w-12 h-6 text-[11px] text-center p-0 mt-1 font-medium bg-white"
                                                title="원하는 순위 숫자 입력 후 Enter"
                                                onClick={(e) => e.stopPropagation()}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleDirectOrderChange(product.productId, Number(e.currentTarget.value));
                                                    }
                                                }}
                                              />
                                          </div>
                                      </td>

                                      <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/admin/products/${product.productId}`)}>{product.productId}</td>
                                      <td className="px-4 py-4 font-medium text-slate-900 cursor-pointer" onClick={() => navigate(`/admin/products/${product.productId}`)}>{product.productName}</td>
                                      <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/admin/products/${product.productId}`)}>{product.categoryName}</td>
                                      <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/admin/products/${product.productId}`)}>{product.productCode || "-"}</td>
                                      <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/admin/products/${product.productId}`)}>{product.brandName || "-"}</td>
                                      <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/admin/products/${product.productId}`)}>{formatPrice(product.price)}</td>
                                      <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/admin/products/${product.productId}`)}>{product.stockQuantity}</td>

                                      <td className="px-4 py-4">
                                          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">{product.status}</span>
                                      </td>
                                      <td className="px-4 py-4">{product.visibleYn}</td>

                                      <td className="px-4 py-4">
                                          <button
                                            onClick={(e) => handleToggleMainExposure(e, product.productId, product.mainExposureYn)}
                                            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${product.mainExposureYn === 'Y' ? 'bg-emerald-500' : 'bg-slate-200'}`}
                                          >
                                              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${product.mainExposureYn === 'Y' ? 'translate-x-5' : 'translate-x-0'}`} />
                                          </button>
                                      </td>

                                      <td className="px-4 py-4">
                                          <button
                                            onClick={(e) => handleToggleBest(e, product.productId, product.bestYn)}
                                            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${product.bestYn === 'Y' ? 'bg-blue-600' : 'bg-slate-200'}`}
                                          >
                                              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${product.bestYn === 'Y' ? 'translate-x-5' : 'translate-x-0'}`} />
                                          </button>
                                      </td>
                                      <td className="px-4 py-4">{product.createdAt}</td>
                                  </tr>
                                ))
                              )}
                              </tbody>
                          </table>
                      </div>

                      {/* 페이징 네비게이션 UI */}
                      {totalPages > 0 && (
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-slate-100 bg-white">
                            <div className="text-sm text-slate-500">
                                총 <span className="font-semibold text-slate-900">{totalElements}</span>개의 상품 (페이지 {page + 1} / {totalPages})
                            </div>

                            <div className="flex items-center gap-1.5">
                                <Button variant="outline" size="sm" className="rounded-lg h-9 px-3 text-xs" disabled={page === 0 || loading} onClick={() => handlePageChange(page - 1)}>이전</Button>

                                <div className="flex items-center gap-1">
                                    {Array.from({ length: totalPages }, (_, i) => i).map((p) => {
                                        if (totalPages > 7 && Math.abs(p - page) > 2 && p !== 0 && p !== totalPages - 1) {
                                            if (p === 1 || p === totalPages - 2) return <span key={p} className="px-1 text-xs text-slate-400">...</span>
                                            return null
                                        }
                                        return (
                                          <Button key={p} variant={p === page ? "default" : "ghost"} size="sm" className={`h-8 w-8 rounded-lg p-0 text-xs font-medium ${p === page ? "bg-slate-900 text-white hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100"}`} onClick={() => handlePageChange(p)} disabled={loading}>
                                              {p + 1}
                                          </Button>
                                        )
                                    })}
                                </div>

                                <Button variant="outline" size="sm" className="rounded-lg h-9 px-3 text-xs" disabled={page >= totalPages - 1 || loading} onClick={() => handlePageChange(page + 1)}>다음</Button>
                            </div>
                        </div>
                      )}
                  </CardContent>
              </Card>
          </div>
      </AdminLayout>
    )
}